import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import secureStorage from '../utils/secureStorage';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import {
  Umbrella, FileText, Calculator, Building2, Wallet, ClipboardCheck,
  Plus, Search, Upload, Download, Eye, Trash2, RefreshCw, ChevronRight,
  AlertCircle, CheckCircle2, Clock, XCircle, IndianRupee, Calendar,
  Heart, Activity, Stethoscope, Hospital, CreditCard, FileCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;

const InsuranceHub = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Policies
  const [policies, setPolicies] = useState([]);
  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [newPolicy, setNewPolicy] = useState({
    insurer_name: '',
    policy_number: '',
    policy_type: 'health',
    plan_name: '',
    sum_insured: '',
    premium_amount: '',
    premium_frequency: 'annual',
    start_date: '',
    end_date: '',
    copay_percentage: 0,
    deductible: 0,
    tpa_name: '',
    notes: ''
  });
  
  // Claims
  const [claims, setClaims] = useState([]);
  const [claimStats, setClaimStats] = useState(null);
  const [showAddClaim, setShowAddClaim] = useState(false);
  const [newClaim, setNewClaim] = useState({
    policy_id: '',
    claim_type: 'hospitalization',
    treatment_type: '',
    hospital_name: '',
    admission_date: '',
    discharge_date: '',
    diagnosis: '',
    treatment_details: '',
    total_bill_amount: '',
    claimed_amount: ''
  });
  
  // Cost Estimator
  const [costEstimate, setCostEstimate] = useState(null);
  const [estimateRequest, setEstimateRequest] = useState({
    treatment_type: '',
    diagnosis: '',
    hospital_type: 'private',
    city: '',
    room_type: 'semi_private',
    expected_days: 3,
    has_insurance: true,
    policy_id: ''
  });
  const [estimating, setEstimating] = useState(false);
  
  // Documents (Wallet)
  const [documents, setDocuments] = useState([]);
  const [wallet, setWallet] = useState({});
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    document_type: 'insurance_card',
    name: '',
    policy_id: '',
    notes: ''
  });
  
  // Hospital Finder
  const [hospitals, setHospitals] = useState([]);
  const [hospitalSearch, setHospitalSearch] = useState({
    city: '',
    specialty: '',
    insurer_name: '',
    cashless_only: true
  });
  const [searchingHospitals, setSearchingHospitals] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashRes, policiesRes, claimsRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/insurance/dashboard`, { headers }),
        axios.get(`${API}/api/insurance/policies`, { headers }),
        axios.get(`${API}/api/insurance/claims`, { headers }),
        axios.get(`${API}/api/insurance/documents`, { headers })
      ]);
      
      setDashboard(dashRes.data);
      setPolicies(policiesRes.data.policies || []);
      setClaims(claimsRes.data.claims || []);
      setClaimStats(claimsRes.data.statistics);
      setDocuments(docsRes.data.documents || []);
      setWallet(docsRes.data.wallet || {});
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load insurance data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPolicy = async () => {
    try {
      const token = secureStorage.getToken();
      const response = await axios.post(`${API}/api/insurance/policies`, newPolicy, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Policy added successfully');
      setShowAddPolicy(false);
      setNewPolicy({
        insurer_name: '', policy_number: '', policy_type: 'health', plan_name: '',
        sum_insured: '', premium_amount: '', premium_frequency: 'annual',
        start_date: '', end_date: '', copay_percentage: 0, deductible: 0,
        tpa_name: '', notes: ''
      });
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add policy');
    }
  };

  const handleSubmitClaim = async () => {
    try {
      const token = secureStorage.getToken();
      const response = await axios.post(`${API}/api/insurance/claims`, newClaim, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Claim submitted! Claim #${response.data.claim_number}`);
      setShowAddClaim(false);
      setNewClaim({
        policy_id: '', claim_type: 'hospitalization', treatment_type: '',
        hospital_name: '', admission_date: '', discharge_date: '',
        diagnosis: '', treatment_details: '', total_bill_amount: '', claimed_amount: ''
      });
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    }
  };

  const handleEstimateCost = async () => {
    setEstimating(true);
    try {
      const token = secureStorage.getToken();
      const response = await axios.post(`${API}/api/insurance/cost-estimate`, estimateRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCostEstimate(response.data.estimate);
      toast.success('Cost estimate generated');
    } catch (error) {
      toast.error('Failed to generate estimate');
    } finally {
      setEstimating(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', uploadData.document_type);
      formData.append('name', uploadData.name || uploadFile.name);
      if (uploadData.policy_id) formData.append('policy_id', uploadData.policy_id);
      if (uploadData.notes) formData.append('notes', uploadData.notes);
      
      await axios.post(`${API}/api/insurance/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Document uploaded successfully');
      setShowUpload(false);
      setUploadFile(null);
      setUploadData({ document_type: 'insurance_card', name: '', policy_id: '', notes: '' });
      loadDashboard();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const handleSearchHospitals = async () => {
    setSearchingHospitals(true);
    try {
      const token = secureStorage.getToken();
      const response = await axios.post(`${API}/api/insurance/hospitals/search`, hospitalSearch, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHospitals(response.data.hospitals || []);
      toast.success(`Found ${response.data.total_found} hospitals`);
    } catch (error) {
      toast.error('Failed to search hospitals');
    } finally {
      setSearchingHospitals(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-blue-100 text-blue-800',
      under_review: 'bg-purple-100 text-purple-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      settled: 'bg-emerald-100 text-emerald-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[80vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
              <Umbrella className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Insurance Hub</h1>
              <p className="text-gray-600">Manage your policies, claims, and healthcare costs</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddPolicy(true)} className="bg-gradient-to-r from-emerald-500 to-teal-600">
              <Plus className="w-4 h-4 mr-2" /> Add Policy
            </Button>
            <Button variant="outline" onClick={() => setShowUpload(true)}>
              <Upload className="w-4 h-4 mr-2" /> Upload Document
            </Button>
          </div>
        </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap gap-2 h-auto p-2 md:grid md:grid-cols-6">
          <TabsTrigger value="dashboard" className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-2">
            <Activity className="w-3 h-3 md:w-4 md:h-4" /> 
            <span className="hidden sm:inline">Dashboard</span>
            <span className="sm:hidden">Home</span>
          </TabsTrigger>
          <TabsTrigger value="policies" className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-2">
            <FileText className="w-3 h-3 md:w-4 md:h-4" /> Policies
          </TabsTrigger>
          <TabsTrigger value="claims" className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-2">
            <ClipboardCheck className="w-3 h-3 md:w-4 md:h-4" /> Claims
          </TabsTrigger>
          <TabsTrigger value="estimator" className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-2">
            <Calculator className="w-3 h-3 md:w-4 md:h-4" /> 
            <span className="hidden sm:inline">Cost Estimator</span>
            <span className="sm:hidden">Cost</span>
          </TabsTrigger>
          <TabsTrigger value="wallet" className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-2">
            <Wallet className="w-3 h-3 md:w-4 md:h-4" /> Wallet
          </TabsTrigger>
          <TabsTrigger value="hospitals" className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-2">
            <Building2 className="w-3 h-3 md:w-4 md:h-4" /> 
            <span className="hidden sm:inline">Find Hospital</span>
            <span className="sm:hidden">Hospitals</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active Policies</p>
                    <p className="text-3xl font-bold">{dashboard?.summary?.active_policies || 0}</p>
                  </div>
                  <Umbrella className="w-10 h-10 text-emerald-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Coverage</p>
                    <p className="text-2xl font-bold">{formatCurrency(dashboard?.summary?.total_coverage)}</p>
                  </div>
                  <Heart className="w-10 h-10 text-red-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Claims Filed</p>
                    <p className="text-3xl font-bold">{dashboard?.summary?.total_claims || 0}</p>
                    <p className="text-xs text-gray-400">{dashboard?.summary?.pending_claims || 0} pending</p>
                  </div>
                  <ClipboardCheck className="w-10 h-10 text-green-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Claim Success Rate</p>
                    <p className="text-3xl font-bold">{dashboard?.summary?.claim_success_rate || 0}%</p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-emerald-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Renewals & Recent Claims */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Renewals */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Upcoming Renewals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.upcoming_renewals?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.upcoming_renewals.map((renewal, idx) => (
                      <div key={`item-${idx}`} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div>
                          <p className="font-medium">{renewal.insurer}</p>
                          <p className="text-sm text-gray-500">{renewal.policy_number}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-amber-100 text-amber-800">
                            {renewal.days_remaining} days left
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No upcoming renewals</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Claims */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5" /> Recent Claims
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.recent_claims?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.recent_claims.slice(0, 4).map((claim, idx) => (
                      <div key={`item-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{claim.claim_number}</p>
                          <p className="text-sm text-gray-500">{claim.treatment_type}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(claim.status)}>{claim.status}</Badge>
                          <p className="text-sm font-medium mt-1">{formatCurrency(claim.claimed_amount)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No claims yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-4">
          {policies.length === 0 ? (
            <Card className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Policies Added</h3>
              <p className="text-gray-500 mb-4">Add your insurance policies to track coverage and claims</p>
              <Button onClick={() => setShowAddPolicy(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add Your First Policy
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {policies.map((policy) => (
                <Card key={policy.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold">{policy.insurer_name}</h3>
                          <Badge className={getStatusColor(policy.status)}>{policy.status}</Badge>
                        </div>
                        <p className="text-gray-500">{policy.plan_name || policy.policy_type}</p>
                        <p className="text-sm text-gray-400">Policy #: {policy.policy_number}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">{formatCurrency(policy.sum_insured)}</p>
                        <p className="text-sm text-gray-500">Sum Insured</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
                      <div>
                        <p className="text-sm text-gray-500">Start Date</p>
                        <p className="font-medium">{new Date(policy.start_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">End Date</p>
                        <p className="font-medium">{new Date(policy.end_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Premium</p>
                        <p className="font-medium">{formatCurrency(policy.premium_amount)} / {policy.premium_frequency}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Copay</p>
                        <p className="font-medium">{policy.copay_percentage || 0}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Claims Tab */}
        <TabsContent value="claims" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-4">
              {claimStats && (
                <>
                  <Badge variant="outline" className="text-base px-4 py-2">
                    Total: {claimStats.total_claims}
                  </Badge>
                  <Badge variant="outline" className="text-base px-4 py-2 bg-yellow-50">
                    Pending: {claimStats.pending}
                  </Badge>
                  <Badge variant="outline" className="text-base px-4 py-2 bg-green-50">
                    Approved: {claimStats.approved}
                  </Badge>
                </>
              )}
            </div>
            <Button onClick={() => setShowAddClaim(true)} disabled={policies.length === 0}>
              <Plus className="w-4 h-4 mr-2" /> File New Claim
            </Button>
          </div>

          {claims.length === 0 ? (
            <Card className="p-12 text-center">
              <ClipboardCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Claims Filed</h3>
              <p className="text-gray-500 mb-4">File a claim when you need to request insurance coverage</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {claims.map((claim) => (
                <Card key={claim.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold">{claim.claim_number}</span>
                          <Badge className={getStatusColor(claim.status)}>{claim.status.replace('_', ' ')}</Badge>
                        </div>
                        <p className="text-gray-600 mt-1">{claim.treatment_type} - {claim.hospital_name}</p>
                        <p className="text-sm text-gray-400">{claim.policy_info?.insurer_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">{formatCurrency(claim.claimed_amount)}</p>
                        {claim.approved_amount && (
                          <p className="text-sm text-green-600">Approved: {formatCurrency(claim.approved_amount)}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Cost Estimator Tab */}
        <TabsContent value="estimator" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Estimator Form */}
            <Card>
              <CardHeader>
                <CardTitle>Treatment Cost Estimator</CardTitle>
                <CardDescription>Get AI-powered cost estimates for medical treatments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Treatment Type</Label>
                    <Select 
                      value={estimateRequest.treatment_type}
                      onValueChange={(v) => setEstimateRequest({...estimateRequest, treatment_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select treatment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="surgery">General Surgery</SelectItem>
                        <SelectItem value="cardiac_surgery">Cardiac Surgery</SelectItem>
                        <SelectItem value="orthopedic_surgery">Orthopedic Surgery</SelectItem>
                        <SelectItem value="maternity">Normal Delivery</SelectItem>
                        <SelectItem value="c_section">C-Section</SelectItem>
                        <SelectItem value="appendectomy">Appendectomy</SelectItem>
                        <SelectItem value="knee_replacement">Knee Replacement</SelectItem>
                        <SelectItem value="angioplasty">Angioplasty</SelectItem>
                        <SelectItem value="dialysis">Dialysis</SelectItem>
                        <SelectItem value="chemotherapy">Chemotherapy (per cycle)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>City</Label>
                    <Select 
                      value={estimateRequest.city}
                      onValueChange={(v) => setEstimateRequest({...estimateRequest, city: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mumbai">Mumbai</SelectItem>
                        <SelectItem value="Delhi">Delhi</SelectItem>
                        <SelectItem value="Bangalore">Bangalore</SelectItem>
                        <SelectItem value="Chennai">Chennai</SelectItem>
                        <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                        <SelectItem value="Kolkata">Kolkata</SelectItem>
                        <SelectItem value="Pune">Pune</SelectItem>
                        <SelectItem value="Ahmedabad">Ahmedabad</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Hospital Type</Label>
                    <Select 
                      value={estimateRequest.hospital_type}
                      onValueChange={(v) => setEstimateRequest({...estimateRequest, hospital_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="super_specialty">Super Specialty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Room Type</Label>
                    <Select 
                      value={estimateRequest.room_type}
                      onValueChange={(v) => setEstimateRequest({...estimateRequest, room_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Ward</SelectItem>
                        <SelectItem value="semi_private">Semi-Private</SelectItem>
                        <SelectItem value="private">Private Room</SelectItem>
                        <SelectItem value="deluxe">Deluxe Suite</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Expected Hospital Stay (days)</Label>
                    <Input 
                      type="number"
                      value={estimateRequest.expected_days}
                      onChange={(e) => setEstimateRequest({...estimateRequest, expected_days: parseInt(e.target.value) || 1})}
                      min="1"
                    />
                  </div>
                  <div>
                    <Label>Select Policy (optional)</Label>
                    <Select 
                      value={estimateRequest.policy_id}
                      onValueChange={(v) => setEstimateRequest({...estimateRequest, policy_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select for insurance calc" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No policy</SelectItem>
                        {policies.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.insurer_name} - {formatCurrency(p.sum_insured)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Diagnosis / Condition (optional)</Label>
                  <Input 
                    placeholder="e.g., Knee osteoarthritis"
                    value={estimateRequest.diagnosis}
                    onChange={(e) => setEstimateRequest({...estimateRequest, diagnosis: e.target.value})}
                  />
                </div>
                
                <Button 
                  onClick={handleEstimateCost} 
                  className="w-full" 
                  disabled={!estimateRequest.treatment_type || !estimateRequest.city || estimating}
                >
                  {estimating ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Calculating...</>
                  ) : (
                    <><Calculator className="w-4 h-4 mr-2" /> Get Estimate</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Estimate Results */}
            <Card>
              <CardHeader>
                <CardTitle>Cost Estimate</CardTitle>
              </CardHeader>
              <CardContent>
                {costEstimate ? (
                  <div className="space-y-6">
                    {/* Total Cost */}
                    <div className="text-center p-6 bg-blue-50 rounded-xl">
                      <p className="text-sm text-gray-600">Estimated Total Cost</p>
                      <p className="text-4xl font-bold text-blue-600">{formatCurrency(costEstimate.estimated_total_cost)}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Range: {formatCurrency(costEstimate.cost_range?.minimum)} - {formatCurrency(costEstimate.cost_range?.maximum)}
                      </p>
                    </div>
                    
                    {/* Insurance Coverage */}
                    {costEstimate.insurance_coverage > 0 && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-green-50 rounded-lg text-center">
                          <p className="text-sm text-gray-600">Insurance Covers</p>
                          <p className="text-2xl font-bold text-green-600">{formatCurrency(costEstimate.insurance_coverage)}</p>
                        </div>
                        <div className="p-4 bg-orange-50 rounded-lg text-center">
                          <p className="text-sm text-gray-600">Out of Pocket</p>
                          <p className="text-2xl font-bold text-orange-600">{formatCurrency(costEstimate.out_of_pocket)}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Cost Breakdown */}
                    <div>
                      <h4 className="font-semibold mb-3">Cost Breakdown</h4>
                      <div className="space-y-2">
                        {Object.entries(costEstimate.cost_breakdown || {}).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-gray-600">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            <span className="font-medium">{formatCurrency(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Tips */}
                    {costEstimate.tips?.length > 0 && (
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" /> Cost-Saving Tips
                        </h4>
                        <ul className="text-sm space-y-1">
                          {costEstimate.tips.map((tip, idx) => (
                            <li key={`item-${idx}`} className="flex items-start gap-2">
                              <span className="text-amber-600">•</span> {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-400">{costEstimate.disclaimer}</p>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Fill in the form and click "Get Estimate"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Wallet Tab */}
        <TabsContent value="wallet" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold">Insurance Wallet</h3>
            <Button onClick={() => setShowUpload(true)}>
              <Upload className="w-4 h-4 mr-2" /> Upload Document
            </Button>
          </div>

          {documents.length === 0 ? (
            <Card className="p-12 text-center">
              <Wallet className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Your Wallet is Empty</h3>
              <p className="text-gray-500 mb-4">Store your insurance cards, policies, and medical documents securely</p>
              <Button onClick={() => setShowUpload(true)}>
                <Upload className="w-4 h-4 mr-2" /> Upload Your First Document
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Group by document type */}
              {Object.entries(wallet).map(([type, docs]) => (
                <Card key={type} className="overflow-hidden">
                  <CardHeader className="bg-gray-50 pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {type === 'insurance_card' && <CreditCard className="w-5 h-5" />}
                      {type === 'policy_document' && <FileText className="w-5 h-5" />}
                      {type === 'medical_report' && <Stethoscope className="w-5 h-5" />}
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      <Badge variant="secondary">{docs.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      {docs.slice(0, 3).map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm truncate flex-1">{doc.name}</span>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost">
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {docs.length > 3 && (
                        <p className="text-sm text-gray-500 text-center">+{docs.length - 3} more</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Hospital Finder Tab */}
        <TabsContent value="hospitals" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Find Cashless Hospitals</CardTitle>
              <CardDescription>Search for network hospitals that accept cashless treatment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>City</Label>
                  <Select 
                    value={hospitalSearch.city}
                    onValueChange={(v) => setHospitalSearch({...hospitalSearch, city: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mumbai">Mumbai</SelectItem>
                      <SelectItem value="Delhi">Delhi</SelectItem>
                      <SelectItem value="Bangalore">Bangalore</SelectItem>
                      <SelectItem value="Chennai">Chennai</SelectItem>
                      <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                      <SelectItem value="Kolkata">Kolkata</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Specialty</Label>
                  <Select 
                    value={hospitalSearch.specialty}
                    onValueChange={(v) => setHospitalSearch({...hospitalSearch, specialty: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="Cardiology">Cardiology</SelectItem>
                      <SelectItem value="Orthopedics">Orthopedics</SelectItem>
                      <SelectItem value="Oncology">Oncology</SelectItem>
                      <SelectItem value="Neurology">Neurology</SelectItem>
                      <SelectItem value="Pediatrics">Pediatrics</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Insurer</Label>
                  <Select 
                    value={hospitalSearch.insurer_name}
                    onValueChange={(v) => setHospitalSearch({...hospitalSearch, insurer_name: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All your insurers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {policies.map(p => (
                        <SelectItem key={p.id} value={p.insurer_name}>{p.insurer_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleSearchHospitals} 
                    className="w-full"
                    disabled={!hospitalSearch.city || searchingHospitals}
                  >
                    {searchingHospitals ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Search
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hospital Results */}
          {hospitals.length > 0 && (
            <div className="grid gap-4">
              {hospitals.map((hospital) => (
                <Card key={hospital.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Hospital className="w-6 h-6 text-blue-600" />
                          <h3 className="text-xl font-semibold">{hospital.name}</h3>
                          <Badge variant="outline">{hospital.type}</Badge>
                          {hospital.cashless && (
                            <Badge className="bg-green-100 text-green-800">Cashless</Badge>
                          )}
                        </div>
                        <p className="text-gray-500">{hospital.address}</p>
                        <div className="flex gap-4 mt-3">
                          <span className="text-sm flex items-center gap-1">
                            ⭐ {hospital.rating} ({hospital.reviews_count} reviews)
                          </span>
                          <span className="text-sm text-gray-500">📍 {hospital.distance}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {hospital.specialties?.slice(0, 4).map((spec, idx) => (
                            <Badge key={`item-${idx}`} variant="secondary">{spec}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Network Insurers:</p>
                        <div className="flex flex-wrap gap-1 mt-1 justify-end max-w-[200px]">
                          {hospital.network_insurers?.slice(0, 3).map((ins, idx) => (
                            <Badge key={`item-${idx}`} variant="outline" className="text-xs">{ins}</Badge>
                          ))}
                        </div>
                        <Button className="mt-4" size="sm">
                          Contact <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Policy Dialog */}
      <Dialog open={showAddPolicy} onOpenChange={setShowAddPolicy}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Insurance Policy</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Insurer Name *</Label>
              <Input 
                placeholder="e.g., Star Health, ICICI Lombard"
                value={newPolicy.insurer_name}
                onChange={(e) => setNewPolicy({...newPolicy, insurer_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Policy Number *</Label>
              <Input 
                placeholder="Policy number"
                value={newPolicy.policy_number}
                onChange={(e) => setNewPolicy({...newPolicy, policy_number: e.target.value})}
              />
            </div>
            <div>
              <Label>Policy Type</Label>
              <Select 
                value={newPolicy.policy_type}
                onValueChange={(v) => setNewPolicy({...newPolicy, policy_type: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="health">Health Insurance</SelectItem>
                  <SelectItem value="life">Life Insurance</SelectItem>
                  <SelectItem value="accident">Personal Accident</SelectItem>
                  <SelectItem value="critical_illness">Critical Illness</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan Name</Label>
              <Input 
                placeholder="e.g., Family Floater Plus"
                value={newPolicy.plan_name}
                onChange={(e) => setNewPolicy({...newPolicy, plan_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Sum Insured (₹) *</Label>
              <Input 
                type="number"
                placeholder="500000"
                value={newPolicy.sum_insured}
                onChange={(e) => setNewPolicy({...newPolicy, sum_insured: e.target.value})}
              />
            </div>
            <div>
              <Label>Premium Amount (₹)</Label>
              <Input 
                type="number"
                placeholder="15000"
                value={newPolicy.premium_amount}
                onChange={(e) => setNewPolicy({...newPolicy, premium_amount: e.target.value})}
              />
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input 
                type="date"
                value={newPolicy.start_date}
                onChange={(e) => setNewPolicy({...newPolicy, start_date: e.target.value})}
              />
            </div>
            <div>
              <Label>End Date *</Label>
              <Input 
                type="date"
                value={newPolicy.end_date}
                onChange={(e) => setNewPolicy({...newPolicy, end_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Copay (%)</Label>
              <Input 
                type="number"
                placeholder="0"
                value={newPolicy.copay_percentage}
                onChange={(e) => setNewPolicy({...newPolicy, copay_percentage: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Deductible (₹)</Label>
              <Input 
                type="number"
                placeholder="0"
                value={newPolicy.deductible}
                onChange={(e) => setNewPolicy({...newPolicy, deductible: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div className="col-span-2">
              <Label>TPA Name</Label>
              <Input 
                placeholder="Third Party Administrator (if any)"
                value={newPolicy.tpa_name}
                onChange={(e) => setNewPolicy({...newPolicy, tpa_name: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea 
                placeholder="Any additional notes..."
                value={newPolicy.notes}
                onChange={(e) => setNewPolicy({...newPolicy, notes: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPolicy(false)}>Cancel</Button>
            <Button 
              onClick={handleAddPolicy}
              disabled={!newPolicy.insurer_name || !newPolicy.policy_number || !newPolicy.sum_insured || !newPolicy.start_date || !newPolicy.end_date}
            >
              Add Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Claim Dialog */}
      <Dialog open={showAddClaim} onOpenChange={setShowAddClaim}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>File Insurance Claim</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Select Policy *</Label>
              <Select 
                value={newClaim.policy_id}
                onValueChange={(v) => setNewClaim({...newClaim, policy_id: v})}
              >
                <SelectTrigger><SelectValue placeholder="Choose policy" /></SelectTrigger>
                <SelectContent>
                  {policies.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.insurer_name} - {p.policy_number} ({formatCurrency(p.sum_insured)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Claim Type</Label>
              <Select 
                value={newClaim.claim_type}
                onValueChange={(v) => setNewClaim({...newClaim, claim_type: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hospitalization">Hospitalization</SelectItem>
                  <SelectItem value="opd">OPD</SelectItem>
                  <SelectItem value="daycare">Daycare</SelectItem>
                  <SelectItem value="maternity">Maternity</SelectItem>
                  <SelectItem value="accident">Accident</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Treatment Type *</Label>
              <Input 
                placeholder="e.g., Knee Surgery"
                value={newClaim.treatment_type}
                onChange={(e) => setNewClaim({...newClaim, treatment_type: e.target.value})}
              />
            </div>
            <div>
              <Label>Hospital Name *</Label>
              <Input 
                placeholder="Hospital name"
                value={newClaim.hospital_name}
                onChange={(e) => setNewClaim({...newClaim, hospital_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Admission Date</Label>
              <Input 
                type="date"
                value={newClaim.admission_date}
                onChange={(e) => setNewClaim({...newClaim, admission_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Discharge Date</Label>
              <Input 
                type="date"
                value={newClaim.discharge_date}
                onChange={(e) => setNewClaim({...newClaim, discharge_date: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Diagnosis *</Label>
              <Input 
                placeholder="Medical diagnosis"
                value={newClaim.diagnosis}
                onChange={(e) => setNewClaim({...newClaim, diagnosis: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Treatment Details</Label>
              <Textarea 
                placeholder="Describe the treatment received..."
                value={newClaim.treatment_details}
                onChange={(e) => setNewClaim({...newClaim, treatment_details: e.target.value})}
              />
            </div>
            <div>
              <Label>Total Bill Amount (₹) *</Label>
              <Input 
                type="number"
                placeholder="100000"
                value={newClaim.total_bill_amount}
                onChange={(e) => setNewClaim({...newClaim, total_bill_amount: e.target.value})}
              />
            </div>
            <div>
              <Label>Claim Amount (₹) *</Label>
              <Input 
                type="number"
                placeholder="100000"
                value={newClaim.claimed_amount}
                onChange={(e) => setNewClaim({...newClaim, claimed_amount: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddClaim(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmitClaim}
              disabled={!newClaim.policy_id || !newClaim.treatment_type || !newClaim.hospital_name || !newClaim.diagnosis || !newClaim.total_bill_amount || !newClaim.claimed_amount}
            >
              Submit Claim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Type</Label>
              <Select 
                value={uploadData.document_type}
                onValueChange={(v) => setUploadData({...uploadData, document_type: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="insurance_card">Insurance Card</SelectItem>
                  <SelectItem value="policy_document">Policy Document</SelectItem>
                  <SelectItem value="claim_form">Claim Form</SelectItem>
                  <SelectItem value="medical_report">Medical Report</SelectItem>
                  <SelectItem value="prescription">Prescription</SelectItem>
                  <SelectItem value="invoice">Invoice/Bill</SelectItem>
                  <SelectItem value="discharge_summary">Discharge Summary</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Document Name</Label>
              <Input 
                placeholder="Give it a name"
                value={uploadData.name}
                onChange={(e) => setUploadData({...uploadData, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Link to Policy (optional)</Label>
              <Select 
                value={uploadData.policy_id}
                onValueChange={(v) => setUploadData({...uploadData, policy_id: v})}
              >
                <SelectTrigger><SelectValue placeholder="Select policy" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {policies.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.insurer_name} - {p.policy_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select File</Label>
              <Input 
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
              {uploadFile && (
                <p className="text-sm text-gray-500 mt-1">
                  Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea 
                placeholder="Any notes about this document..."
                value={uploadData.notes}
                onChange={(e) => setUploadData({...uploadData, notes: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={!uploadFile}>
              <Upload className="w-4 h-4 mr-2" /> Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default InsuranceHub;
